// apps/vendor/src/pages/settings/ChangePasswordPage.tsx
//
// Phase 1 Task 47 — Change password (sensitive route, settings area).
//
// Per CONTEXT + SPECS / AC-08:
//   - currentPassword + newPassword + confirm form, policy live-checked.
//   - POST /api/v1/auth/change-password.
//   - On 401 with envelope code `AUTH_PERM_VERSION_STALE` → call
//     useAuth.refresh and retry the call exactly once. the
//     server bumps `permVersion` on sensitive operations; refresh re-binds
//     the JWT so the second attempt clears the stale check.

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  useAuth,
  SettingsLayout,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

// ---------------------------------------------------------------------------
// Password policy — mirrors @printway/zod-schemas PasswordSchema.
// ---------------------------------------------------------------------------

const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

interface PolicyCheck {
  id: 'len' | 'upper' | 'num' | 'special';
  test: (pw: string) => boolean;
}

// Password rule meta — labels are resolved via i18n inside the component.
const PASSWORD_RULES: readonly PolicyCheck[] = [
  { id: 'len', test: (pw) => pw.length >= 12 },
  { id: 'upper', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'num', test: (pw) => /[0-9]/.test(pw) },
  { id: 'special', test: (pw) => SPECIAL_CHARS_RE.test(pw) },
];

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function buildChangePasswordSchema(t: TFn) {
  return z
    .object({
      currentPassword: z.string().min(1, t('password.validationCurrentRequired')),
      newPassword: z
        .string()
        .min(12, t('password.validationMinLen'))
        .refine((p) => /[A-Z]/.test(p), t('password.validationUpper'))
        .refine((p) => /[0-9]/.test(p), t('password.validationNum'))
        .refine(
          (p) => SPECIAL_CHARS_RE.test(p),
          t('password.validationSpecial'),
        ),
      confirmPassword: z.string().min(1, t('password.validationConfirmRequired')),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('password.validationMismatch'),
      path: ['confirmPassword'],
    })
    .refine((d) => d.currentPassword !== d.newPassword, {
      message: t('password.validationSameAsOld'),
      path: ['newPassword'],
    });
}
interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ---------------------------------------------------------------------------
// Envelope helpers — narrow shape returned by /api/v1/auth/change-password.
// ---------------------------------------------------------------------------

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error.code ?? null;
  }
  return null;
}

interface PostResult {
  ok: boolean;
  status: number;
  errorCode: string | null;
}

async function postChangePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<PostResult> {
  const client = apiClient();
  const { response, error } = await (
    client as unknown as {
      POST: (
        path: string,
        init: { body: unknown },
      ) => Promise<{
        data?: unknown;
        error?: unknown;
        response: Response;
      }>;
    }
  ).POST('/api/v1/auth/change-password', { body });
  return {
    ok: response.ok,
    status: response.status,
    errorCode: extractErrorCode(error),
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ServerErrorState =
  | null
  | { kind: 'invalid' }
  | { kind: 'reused' }
  | { kind: 'generic'; message: string };

export function ChangePasswordPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);
  const [serverError, setServerError] = useState<ServerErrorState>(null);
  const [submitted, setSubmitted] = useState(false);

  const schema = useMemo(() => buildChangePasswordSchema(t as TFn), [t]);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const RULE_LABEL_KEY: Record<PolicyCheck['id'], string> = {
    len: 'password.ruleLen',
    upper: 'password.ruleUpper',
    num: 'password.ruleNum',
    special: 'password.ruleSpecial',
  };

  const newPassword = watch('newPassword') ?? '';
  const checks = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        id: rule.id,
        label: t(RULE_LABEL_KEY[rule.id]),
        passed: rule.test(newPassword),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newPassword, t],
  );

  const onSubmit = async (data: ChangePasswordInput): Promise<void> => {
    setServerError(null);
    setSubmitted(false);
    const body = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    };

    try {
      let res = await postChangePassword(body);

      //  Tier A — perm-version stale → refresh once + retry.
      if (
        !res.ok &&
        res.status === 401 &&
        res.errorCode === 'AUTH_PERM_VERSION_STALE'
      ) {
        await refresh();
        res = await postChangePassword(body);
      }

      if (res.ok) {
        setSubmitted(true);
        reset({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        return;
      }

      if (res.errorCode === 'AUTH_PASSWORD_REUSED') {
        setServerError({ kind: 'reused' });
        return;
      }
      if (
        res.errorCode === 'AUTH_INVALID_CREDENTIALS' ||
        res.status === 401 ||
        res.status === 403
      ) {
        setServerError({ kind: 'invalid' });
        return;
      }
      setServerError({
        kind: 'generic',
        message:
          res.errorCode ?? t('password.errorGenericHttp', { status: String(res.status) }),
      });
    } catch (err) {
      setServerError({
        kind: 'generic',
        message:
          err instanceof Error ? err.message : t('password.errorUnknown'),
      });
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="security">
      <Card>
        <CardHeader>
          <CardTitle>{t('password.cardTitle')}</CardTitle>
          <CardDescription>
            {t('password.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="currentPassword">
                {t('password.currentLabel')}
              </FormLabel>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                {...register('currentPassword')}
              />
              {errors.currentPassword ? (
                <FormMessage>{errors.currentPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <FormField>
              <FormLabel htmlFor="newPassword">{t('password.newLabel')}</FormLabel>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
              />
              {errors.newPassword ? (
                <FormMessage>{errors.newPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <ul className="space-y-1 text-xs" aria-label={t('password.rulesAria')}>
              {checks.map((c) => (
                <li
                  key={c.id}
                  className={
                    c.passed
                      ? 'text-emerald-600'
                      : 'text-pw-text-muted'
                  }
                >
                  <span aria-hidden="true">{c.passed ? '✓' : '○'}</span>{' '}
                  {c.label}
                </li>
              ))}
            </ul>

            <FormField>
              <FormLabel htmlFor="confirmPassword">
                {t('password.confirmLabel')}
              </FormLabel>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <FormMessage>{errors.confirmPassword.message}</FormMessage>
              ) : null}
            </FormField>

            {serverError?.kind === 'invalid' ? (
              <FormMessage>
                {t('password.errorInvalid')}
              </FormMessage>
            ) : null}
            {serverError?.kind === 'reused' ? (
              <FormMessage>
                {t('password.errorReused')}
              </FormMessage>
            ) : null}
            {serverError?.kind === 'generic' ? (
              <FormMessage>{serverError.message}</FormMessage>
            ) : null}
            {submitted ? (
              <p className="text-sm font-medium text-emerald-600">
                {t('password.success')}
              </p>
            ) : null}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('password.submitting') : t('password.submit')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}

export default ChangePasswordPage;
